import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { emit } from '../../platform/analytics/analytics'
import { Button } from '../../platform/primitives/Button'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Panel } from '../../platform/primitives/Panel'
import { SearchField } from '../../platform/primitives/SearchField'
import { HelpArticle } from './HelpArticle'
import { SHARE_URL, contactHref, searchArticles, suggestHref } from './support'
import { useOnce } from '../../platform/analytics/useOnce'
import './help.css'

/** How long the query must sit still before a search is counted. Intake D3. */
const SETTLE_MS = 800
const NO_RESULTS = 'No articles match that search.'

type ShareNote = 'copied' | 'manual' | null

/**
 * `/help` and `/help/:slug` (M14-R1..R3, R12, R13). `ChromeLayout` renders the
 * page `<h1>`, so this starts at `<h2>`. Static content: nothing fetches, so
 * there is no `<Async>` here. Mounted at `/help/*`; the sub-path is the slug.
 */
export function Help() {
  const slug = useParams()['*'] ?? ''
  return slug === '' ? <HelpCentre /> : <HelpArticle key={slug} slug={slug} />
}

function HelpCentre() {
  const { pathname } = useLocation()
  const [query, setQuery] = useState('')
  const [note, setNote] = useState<ShareNote>(null)
  const results = searchArticles(query)

  useOnce(useCallback(() => emit('help_viewed'), []))

  // Settled search only, and lengths only — never the text (rule 7).
  useEffect(() => {
    const length = query.trim().length
    if (length === 0) return
    const timer = setTimeout(() => {
      const count = searchArticles(query).length
      emit('help_search', { query_length: length, result_count: count })
      if (count === 0) emit('help_no_results', { query_length: length })
    }, SETTLE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const contact = contactHref({ mode: import.meta.env.MODE, userAgent: navigator.userAgent, path: pathname })
  const onContact = () => emit('contact_started')

  async function share() {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: 'TrackE5', url: SHARE_URL })
      } catch {
        // Dismissing the share sheet rejects; that is a choice, not a failure.
      }
      return
    }
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(SHARE_URL)
        setNote('copied')
        return
      } catch {
        // Denied: fall through to showing the link.
      }
    }
    setNote('manual')
  }

  const trimmed = query.trim()

  return (
    <div className="help">
      <Panel className="help__section">
        <h2 className="help__h">Search help</h2>
        <SearchField className="help__search" label="Search help articles" value={query} onChange={setQuery} placeholder="Search help articles" />
        {/* Mounted from the first render, so the count is an update to a live region, not an insertion. */}
        <p role="status" className="help__count">
          {trimmed === '' ? '' : results.length === 1 ? '1 article' : `${results.length} articles`}
        </p>
        {results.length === 0 ? (
          <p className="help__empty">
            <span>{NO_RESULTS}</span>{' '}
            <InlineLink to={contact} onClick={onContact}>
              Contact support
            </InlineLink>
          </p>
        ) : (
          <ul className="help__list">
            {results.map((a) => (
              <li key={a.slug} className="help__item">
                <InlineLink to={`/help/${a.slug}`} className="help__link">{a.title}</InlineLink>
                <span className="help__summary">{a.summary}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel className="help__section">
        <h2 className="help__h">Still stuck?</h2>
        <div className="help__actions">
          <ButtonLink variant="primary" size="page" href={contact} onClick={onContact}>
            Contact support
          </ButtonLink>
          <ButtonLink variant="outline" size="page" href={suggestHref()} onClick={() => emit('feature_suggested')}>
            Suggest a feature
          </ButtonLink>
          <Button variant="outline" size="page" onClick={() => void share()}>
            Share TrackE5
          </Button>
        </div>
        <p role="status" className="help__note">
          {note === 'copied' ? 'Link copied' : note === 'manual' ? `Copy ${SHARE_URL} to share it` : ''}
        </p>
      </Panel>

      <Panel className="help__section">
        <h2 className="help__h">Legal and reference</h2>
        <ul className="help__list">
          <li className="help__item">
            <InlineLink to="/legal" className="help__link">Privacy, terms and acknowledgements</InlineLink>
          </li>
        </ul>
      </Panel>
    </div>
  )
}
